import React from 'react'
import { Outlet, useLocation, useParams } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '@/hooks/reduxHooks'
import { useTranslation } from 'react-i18next'

import { getDeviceData } from '@/store/slices/device-slice'
import SideButtonsContainer from '@/features/device/components/SideButtonsContainer'
import { Title, Loader } from '@/components'
import useDeviceMeasurementsHistory from '@/hooks/useDeviceMeasurementsHistory'
import { getTemplateByLanguage } from '@/utils/utils'
import { getTemplateForDeviceData } from '@/store/slices/templates-slice'

const DeviceLayout = () => {
  const { id } = useParams()
  const location = useLocation()
  const dispatch = useAppDispatch()

  const device = useAppSelector((state) => (id ? state.device.allDevicesData[id] : undefined))
  const allTemplatesData = useAppSelector((state) => state.templates.allTemplatesData)
  const devicesDetailsLoading = useAppSelector((state) => state.device.devicesDetailsStatus === 'loading')
  const isLoadingTemplate = useAppSelector((state) => state.templates.templateDetailsStatus === 'loading')

  const { i18n } = useTranslation()

  const selectedLanguage = i18n.language.toUpperCase()

  const selectedTemplate = React.useMemo(() => {
    if (!device?.deviceTemplates) return
    return getTemplateByLanguage(device.deviceTemplates, allTemplatesData, selectedLanguage)
  }, [device?.deviceTemplates, allTemplatesData, selectedLanguage])

  const { deviceMeasurementsHistory } = useDeviceMeasurementsHistory(id, selectedTemplate?.id, location.key)

  React.useEffect(() => {
    if (!id) return
    if (device?.deviceTemplates) return
    dispatch(getDeviceData(id)).unwrap()
  }, [dispatch, id])

  React.useEffect(() => {
    if (device?.id) {
      const templatesForDevices = Object.values(allTemplatesData).find(
        ({ id, language }) => device?.deviceTemplates?.includes(id) && language === selectedLanguage,
      )

      if (!templatesForDevices || !templatesForDevices.templateConfig)
        dispatch(getTemplateForDeviceData({ deviceId: device.id, language: selectedLanguage }))
    }
  }, [device?.id, i18n.language])

  return (
    <>
      <Title device={device} />
      <div className='flex gap-4 flex-col-reverse 2xl:flex-row'>
        {devicesDetailsLoading || isLoadingTemplate ? (
          <Loader />
        ) : (
          <Outlet context={{ deviceMeasurementsHistory, device, id, selectedTemplate }} />
        )}
        <SideButtonsContainer id={id!} />
      </div>
    </>
  )
}

export default DeviceLayout
